// The explicit attribute is required so this module also loads under Node's ESM
// loader, which the Playwright e2e suite uses to read the navigation tree directly.
import wpfNavLabels from './wpfNavLabels.json' with { type: 'json' };

const labels = wpfNavLabels as Record<string, string>;

export function labelForWpfKey(key: string): string {
  return labels[key] ?? key;
}

/**
 * WPF `NavigationDescriptor.Key` → canonical React path.
 * Keys not listed use `/p/:key` (generic “coming soon” screen).
 */
export const WPF_PATH_OVERRIDES: Record<string, string> = {
  HomeHub: '/home',
  SummaryDashboard: '/',
  SearchAllAssets: '/instruments',
  SearchPortfolios: '/portfolios',
  CreatePortfolio: '/portfolios/new',
  SearchAccounts: '/accounts',
  OpenAccount: '/accounts/new',
  SearchCustodians: '/custodians',
  CreateCustodian: '/custodians/new',
  SearchInstrument: '/instruments',
  // Leftover WPF `NbaInstrument` bookmarks land on the real-asset book.
  NbaInstrument: '/reports/dashboards/real-assets',
  DefineEquity: '/instruments/new/equity',
  DefineCurrency: '/instruments/new/currency',
  DefineFund: '/instruments/new/fund',
  DefineFixedRateInstrument: '/instruments/new/bond',
  DefineZeroCouponBond: '/instruments/new/bond/zero-coupon',
  DefineFloatingRateBond: '/instruments/new/bond/floating-rate',
  DefineCommodity: '/instruments/new/commodity',
  DefineRealAsset: '/realassets/new',
  DefineFxCurrencyPair: '/instruments/new/fx-pair',
  DefineTimeDeposit: '/instruments/new/time-deposit',
  DefineLoan: '/instruments/new/loan',
  DefineIndex: '/instruments/new/index',
  DefineIndexFutures: '/instruments/new/index-futures',
  DefineStockFutures: '/instruments/new/stock-futures',
  DefineCurrencyFutures: '/instruments/new/currency-futures',
  DefineCommodityFutures: '/instruments/new/commodity-futures',
  DefineIndexOption: '/instruments/new/index-option',
  DefineStockOption: '/instruments/new/stock-option',
  DefineOptionOnFutures: '/instruments/new/option-on-futures',
  DefineFxOption: '/instruments/new/fx-option',
  SearchDeals: '/deals/search',
  CaptureBuy: '/deals/capture/buy',
  CaptureSell: '/deals/capture/sell',
  CaptureDeposit: '/deals/capture/deposit',
  CaptureWithdraw: '/deals/capture/withdraw',
  CaptureFxSpot: '/deals/capture/fx-spot',
  CaptureFxForward: '/deals/capture/fx-forward',
  CaptureBuyOtcOption: '/deals/capture/otc-option/buy',
  CaptureSellOtcOption: '/deals/capture/otc-option/sell',
  PlaceTermDeposit: '/deals/capture/term-deposit/place',
  BreakTermDeposit: '/deals/capture/term-deposit/break',
  DrawdownLoan: '/deals/capture/loan/drawdown',
  RepayLoanEarly: '/deals/capture/loan/repay',
  CaptureAcquire: '/realassets/acquire',
  CaptureDispose: '/realassets/sell',
  CaptureGift: '/deals/capture/gift',
  CaptureInheritance: '/deals/capture/inheritance',
  CaptureIncome: '/deals/capture/income',
  CaptureExpense: '/deals/capture/expense',
  ReceiveLong: '/deals/capture/receive-long',
  ReceiveShort: '/deals/capture/receive-short',
  DeliverLong: '/deals/capture/deliver-long',
  DeliverShort: '/deals/capture/deliver-short',
  AiDealCapture: '/deals/capture/ai',
  Holdings: '/reports/dashboards/holdings-positions?tile=current-holdings',
  BondHoldings: '/reports/dashboards/holdings-positions?tile=bond-holdings',
  NbaHoldings: '/reports/dashboards/holdings-positions?tile=real-assets',
  RealAssetsWorth: '/reports/dashboards/holdings-positions?tile=real-assets-worth',
  RealAssetsCapitalAccount: '/reports/dashboards/holdings-positions?tile=real-assets-capital-account',
  HoldingsPositionsDashboard: '/reports/dashboards/holdings-positions',
  AssetReconciliation: '/reports/dashboards/reconciliation?tile=asset-reconciliation',
  BondReconciliation: '/reports/dashboards/reconciliation?tile=bond-reconciliation',
  PerformanceDashboard: '/reports/dashboards/performance',
  PLDashboard: '/reports/dashboards/pl',
  TransactionsCashDashboard: '/reports/dashboards/transactions-cash',
  UpcomingEvents: '/reports/dashboards/transactions-cash?tile=upcoming-events',
  CashLedgerPivot: '/reports/dashboards/transactions-cash?tile=cash-ledger-pivot',
  ClientStatementDashboard: '/reports/dashboards/client-statement',
  AnalyticsDashboard: '/reports/dashboards/analytics',
  ReconciliationDashboard: '/reports/dashboards/reconciliation',
  ReconciliationWorkbench: '/reports/dashboards/reconciliation-workbench',
  WhatIfScenarioDashboard: '/reports/dashboards/what-if',
  // Retired composite. Wealth is `/portfolios/:portfolioId/wealth` and this map has no portfolio id.
  UnifiedDashboard: '/p/UnifiedDashboard',
  PrivateEquityDashboard: '/reports/dashboards/private-equity',
  RealAssetsDashboard: '/reports/dashboards/real-assets',
  SearchParticipations: '/private-equity/participations',
  DefinePrivateEquity: '/private-equity/define',
  CapturePrivateEquityActivity: '/private-equity/capture-activity',
  ReceivePrivateEquity: '/private-equity/receive',
  DynamicDashboard: '/reports/dashboards/dynamic',
  UserDashboardList: '/reports/dashboards/my',
  DashboardDesigner: '/reports/dashboards/designer',
  ConfigurableReporting: '/reports/configurable',
  ReportExplorer: '/reports/explorer',
  PLAnalytics: '/reports/pl-analytics',
  PLAnalyticsRollupSummary: '/reports/pl-analytics-summary',
  PositionPerformance: '/reports/position-performance',
  TermDeposits: '/reports/term-deposits',
  Loans: '/reports/loans',
  ThemeSelection: '/settings/theme',
  SignalRLogs: '/system/signalr-logs',
  NotificationHistory: '/system/notification-history',
  MarketPricesBrowser: '/market-data/prices',
  ForexRatesBrowser: '/market-data/fx',
  CapturePrice: '/market-data/capture/price',
  CaptureFxRate: '/market-data/capture/fx-rate',
  HoldingPrices: '/market-data/position-prices',
  HoldingPeriodPrices: '/market-data/position-prices?mode=period',
  CaptureCashDividend: '/corporate-actions/capture/cash-dividend',
  CaptureStockDividend: '/corporate-actions/capture/stock-dividend',
  CaptureReturnOfCapital: '/corporate-actions/capture/return-of-capital',
  CaptureCapitalGainsDistribution: '/corporate-actions/capture/capital-gains-distribution',
  CaptureStockSplit: '/corporate-actions/capture/stock-split',
  CaptureReverseStockSplit: '/corporate-actions/capture/reverse-stock-split',
  CaptureMerger: '/corporate-actions/capture/merger',
  CaptureShareClassConversion: '/corporate-actions/capture/share-class-conversion',
  CaptureSpinOff: '/corporate-actions/capture/spin-off',
  SearchCorporateActions: '/corporate-actions/search',
  SearchExchanges: '/exchanges',
  CreateExchange: '/exchanges/new',
  ClientList: '/clients',
  CreateClient: '/clients/new',
  OnboardingDashboard: '/clients/onboarding/dashboard',
  OnboardingPipeline: '/clients/onboarding/pipeline',
  OnboardingAging: '/clients/onboarding/aging',
  ScreeningOutcomes: '/clients/onboarding/screening-outcomes',
  DocumentsStatus: '/clients/onboarding/documents-status',
  KycDocumentVerification: '/clients/kyc/document-verification',
  ComplianceScreeningQueue: '/clients/compliance/screening-queue',
  OnboardingReviewQueue: '/clients/onboarding/review-queue',
  RiskQuestionnaireTemplates: '/clients/risk-questionnaire-templates',
  OnboardClient: '/clients/onboard',
  BeneficialOwnershipDiagram: '/clients/beneficial-ownership',
  OwnershipDiagramEditor: '/clients/ownership-diagram-editor',
  BrowseFeeRules: '/fees/rules',
  CreateFeeRule: '/fees/rules/new',
  EditFeeRule: '/fees/rules/:ruleId/edit',
  FeeDeviation: '/fees/reports/deviation',
  ReclaimableCharges: '/fees/reports/reclaimable',
  Retrocession: '/fees/reports/retrocession',
  ComplianceDashboard: '/compliance/dashboard',
  ComplianceRulesDashboard: '/compliance/rules',
  CreateComplianceRule: '/compliance/rules/new',
  CreateCompliancePolicy: '/compliance/policies/new',
  SuggestComplianceRules: '/compliance/suggest-rules',
  ClassificationSchemes: '/instruments/classification-schemes',
  DefineCompositeBenchmark: '/benchmarks/composite',
  AssetClassBenchmarks: '/benchmarks/asset-class',
  FuturesContractSearch: '/instruments/search/futures-contracts',
  OptionChainSearch: '/instruments/search/option-chains',
};

export function pathForWpfKey(key: string): string {
  return WPF_PATH_OVERRIDES[key] ?? `/p/${encodeURIComponent(key)}`;
}
